import { Link, type LinkProps } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * The reader's 20px bordered square (the mock's `nl-back`): the inset fill and the default hairline
 * at rest, the raised fill on hover, the chevron nudging 2px the way it points. Dead at 40%.
 * The hit area is a 36px square around it.
 */
const BUTTON =
  "group/nav relative inline-flex size-5 shrink-0 items-center justify-center border border-border bg-muted text-foreground transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-popover disabled:cursor-default disabled:bg-muted disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"

const CHEVRON = 'size-4 transition-[translate] duration-med ease-out'

/** A pager button: `back` is the previous page, `next` the next. */
export function IssuePagerButton({
  direction,
  label,
  disabled,
  onClick,
}: {
  direction: 'back' | 'next'
  label: string
  disabled: boolean
  onClick: () => void
}) {
  const Icon = direction === 'back' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={BUTTON}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          CHEVRON,
          direction === 'back'
            ? 'group-enabled/nav:group-hover/nav:motion-safe:-translate-x-(--motion-nudge)'
            : 'group-enabled/nav:group-hover/nav:motion-safe:translate-x-(--motion-nudge)',
        )}
      />
    </button>
  )
}

/** The back arrow at the head of the reader, a link to the archive. */
export function IssueBackLink({ to }: { to: NonNullable<LinkProps['to']> }) {
  return (
    <Link to={to} aria-label="Back" className={BUTTON}>
      <ChevronLeft
        aria-hidden="true"
        className={cn(CHEVRON, 'group-hover/nav:motion-safe:-translate-x-(--motion-nudge)')}
      />
    </Link>
  )
}
